// Admin access-control wire contract — the shapes the `/admin/access` table
// exchanges, shared between apps/server (`http/admin.ts`) and apps/web
// (`admin/useAccess.ts`). Mirrors the `@loom/shared/analytics` split: the
// server computes, the web renders, one Zod source of truth.
//
// Content-free except for the ON-DEMAND display names (resolved from GitLab on
// the admin's own token at read time, never persisted — the `resolveNamedRows`
// pattern). The durable store holds only ids/enums/timestamps.

import { z } from "zod";

import { CostBySurfaceSchema, UsdSchema } from "../analytics/index.js";
import { forgeOfProjectId, projectIdSchema } from "../forge/index.js";

export const AccessStatusSchema = z.enum(["pending", "approved", "blocked"]);
export type AccessStatusWire = z.infer<typeof AccessStatusSchema>;

export const AccessLevelSchema = z.enum(["create_only", "full"]);
export type AccessLevelWire = z.infer<typeof AccessLevelSchema>;

/** One project grant in the admin table. */
export const AdminGrantSchema = z.object({
  projectId: z.number().int(),
  /** Resolved project path (best-effort, null on lookup failure). */
  projectPath: z.string().nullable(),
  level: AccessLevelSchema,
  grantedBy: z.string(),
  updatedAtMs: z.number(),
});
export type AdminGrant = z.infer<typeof AdminGrantSchema>;

/** One user row in the admin table, joined with grants + display name. */
export const AdminUserRowSchema = z.object({
  userId: z.string(),
  gitlabHost: z.string(),
  status: AccessStatusSchema,
  /** Resolved GitLab display name (best-effort, null on lookup failure). */
  displayName: z.string().nullable(),
  firstSeenAtMs: z.number(),
  updatedAtMs: z.number(),
  isAdmin: z.boolean(),
  grants: z.array(AdminGrantSchema),
});
export type AdminUserRow = z.infer<typeof AdminUserRowSchema>;

export const AdminUsersResponseSchema = z.object({
  users: z.array(AdminUserRowSchema),
});
export type AdminUsersResponse = z.infer<typeof AdminUsersResponseSchema>;

/** PATCH /api/admin/access/users/:userId */
export const PatchUserStatusBodySchema = z.object({
  status: AccessStatusSchema,
});
export type PatchUserStatusBody = z.infer<typeof PatchUserStatusBodySchema>;

/** PUT /api/admin/access/users/:userId/projects/:projectId */
export const PutProjectGrantBodySchema = z.object({
  level: AccessLevelSchema,
});
export type PutProjectGrantBody = z.infer<typeof PutProjectGrantBodySchema>;

/**
 * GET /api/admin/code-map/:projectId — the operator's provisioning status
 * view for a project's Projekt-Karte. Same `hasMap` discriminant as
 * `@loom/shared/code-map`'s `projectCodeMapResponseSchema` (the create-flow's
 * own read), but the `true` branch carries the operator-facing summary
 * fields (`nodeCount`, timestamps, the SHA the map was generated against)
 * instead of the full `tree` — the admin surface shows status, not the panel.
 */
export const AdminCodeMapStatusSchema = z.discriminatedUnion("hasMap", [
  z.object({ hasMap: z.literal(false) }),
  z.object({
    hasMap: z.literal(true),
    version: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    defaultBranchSha: z.string().nullable(),
    generatedAtMs: z.number(),
    updatedAtMs: z.number(),
  }),
]);
export type AdminCodeMapStatus = z.infer<typeof AdminCodeMapStatusSchema>;

/** POST /api/admin/code-map/:projectId/generate — success response. */
export const AdminCodeMapGenerateResponseSchema = z.object({
  ok: z.literal(true),
  version: z.number().int().nonnegative(),
  nodeCount: z.number().int().nonnegative(),
  droppedCodePaths: z.number().int().nonnegative(),
  truncated: z.boolean(),
  /** How many repo files reached the model. Shown alongside the map so
   *  coverage is visible up front, not inferred from a truncation note. */
  scannedFiles: z.number().int().nonnegative(),
  /** How many files the repo walk found. Equals `scannedFiles` unless the
   *  prompt budget cut the list. */
  totalFiles: z.number().int().nonnegative(),
});
export type AdminCodeMapGenerateResponse = z.infer<
  typeof AdminCodeMapGenerateResponseSchema
>;

/**
 * Who an admin assigns an Anthropic key to: a Loom user, or a GitLab project
 * (a GitHub repository has none).
 */
export const AnthropicKeyOwnerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), userId: z.string().regex(/^\d+$/) }),
  z.object({
    kind: z.literal("project"),
    projectId: projectIdSchema.refine((n) => forgeOfProjectId(n) === "gitlab", {
      message: "a project key is GitLab only",
    }),
  }),
]);
export type AnthropicKeyOwner = z.infer<typeof AnthropicKeyOwnerSchema>;

/** GET /api/anthropic-key/status: `assigned` is a key an admin assigned from
 *  Loom's own org, which only an admin removes. */
export const AnthropicKeyStatusSchema = z.object({
  connected: z.boolean(),
  assigned: z.boolean(),
});
export type AnthropicKeyStatus = z.infer<typeof AnthropicKeyStatusSchema>;

/** The days a key's spend covers, up to now. */
export const ANTHROPIC_KEY_SPEND_DAYS = 30;

/** A key's spend in Loom's own ledger (`model_spend`) over the last
 *  {@link ANTHROPIC_KEY_SPEND_DAYS} days, priced the way the analytics fold
 *  prices it: the total and its split by surface. */
export const AnthropicKeySpendSchema = z.object({
  totalUsd: UsdSchema,
  bySurface: CostBySurfaceSchema,
});
export type AnthropicKeySpend = z.infer<typeof AnthropicKeySpendSchema>;

/** GET /api/anthropic-key/spend: the caller's own assigned key only; null
 *  when no key is assigned to the caller or the ledger is off. */
export const AnthropicKeySpendResponseSchema = z.object({
  spend: AnthropicKeySpendSchema.nullable(),
});
export type AnthropicKeySpendResponse = z.infer<typeof AnthropicKeySpendResponseSchema>;

/** One stored key on the admin page: ids only, never the key. `spendUsd` is
 *  an assigned key's ledger total over the last
 *  {@link ANTHROPIC_KEY_SPEND_DAYS} days, `monthUsd` this calendar month's in
 *  Zurich up to now; null for a row that is not assigned or where the ledger
 *  is off. `monthlyLimitUsd` is the admin's monthly limit: past it, Focus
 *  shows a banner. */
export const AdminAnthropicKeyRowSchema = z.object({
  owner: AnthropicKeyOwnerSchema,
  keyId: z.string(),
  assigned: z.boolean(),
  updatedAtMs: z.number(),
  spendUsd: UsdSchema.nullable(),
  monthUsd: UsdSchema.nullish(),
  monthlyLimitUsd: UsdSchema.nullish(),
});
export type AdminAnthropicKeyRow = z.infer<typeof AdminAnthropicKeyRowSchema>;

/** GET /api/admin/anthropic-keys: `assignable` is false in an env without
 *  the org's admin key, where an admin lists and removes keys but assigns
 *  none. */
export const AdminAnthropicKeysResponseSchema = z.object({
  keys: z.array(AdminAnthropicKeyRowSchema),
  assignable: z.boolean(),
});
export type AdminAnthropicKeysResponse = z.infer<typeof AdminAnthropicKeysResponseSchema>;

/** The Console name an assignable key starts with: an unassign switches the
 *  key off, and every key Loom runs on is named `loom-…`, so neither one of
 *  those nor a key another team of the org uses is ever assigned. */
export const ASSIGNABLE_KEY_NAME_PREFIX = "loom-assign-";

/** POST /api/admin/anthropic-keys */
export const AssignAnthropicKeyBodySchema = z.object({
  owner: AnthropicKeyOwnerSchema,
  key: z.string(),
});
export type AssignAnthropicKeyBody = z.infer<typeof AssignAnthropicKeyBodySchema>;

/** POST /api/admin/anthropic-keys/delete */
export const UnassignAnthropicKeyBodySchema = z.object({
  owner: AnthropicKeyOwnerSchema,
});
export type UnassignAnthropicKeyBody = z.infer<typeof UnassignAnthropicKeyBodySchema>;

/** POST /api/admin/anthropic-keys/limit: null clears it. Under a million,
 *  so it fits the `numeric(12,6)` column. */
export const SetAnthropicKeyLimitBodySchema = z.object({
  owner: AnthropicKeyOwnerSchema,
  monthlyLimitUsd: UsdSchema.refine((v) => Number(v) > 0 && Number(v) < 1_000_000).nullable(),
});
export type SetAnthropicKeyLimitBody = z.infer<typeof SetAnthropicKeyLimitBodySchema>;

/** GET /api/projects/:projectId/key-limit: the key that pays for the caller
 *  there (their own, else the project's), its month to date (Zurich) and its
 *  limit; null when that key has no limit, no key is assigned, or the ledger
 *  is off or its read fails. */
export const KeyLimitResponseSchema = z.object({
  month: z
    .object({
      owner: z.enum(["user", "project"]),
      monthUsd: UsdSchema,
      monthlyLimitUsd: UsdSchema,
    })
    .nullable(),
});
export type KeyLimitResponse = z.infer<typeof KeyLimitResponseSchema>;
export type KeyLimitMonth = NonNullable<KeyLimitResponse["month"]>;
